import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageListType } from 'react-images-uploading';
import styles from './compound-details.module.scss';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import ImageUploading from 'react-images-uploading';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';

export interface CompoundDetailsItem {
  title: string;
  description: string;
  titleAr?: string;
  descriptionAr?: string;
  image?: string;
  id?: string;
}

export interface CompoundDetailsProps {
  data: {
    propertyId: string;
    compound?: CompoundDetailsItem[];
  };
  eventHandlers: {
    uploadCompoundDetails: (
      title: string,
      description: string,
      file: File,
      titleAr: string,
      descriptionAr: string
    ) => void;
    updateCompoundDetails: (
      compoundId: string,
      title: string,
      description: string,
      titleAr: string,
      descriptionAr: string,
      file?: File
    ) => void;
    deleteCompoundDetails: (id: string) => void;
  };
}

interface FormElement<T> {
  value: T;
  isValid: boolean;
  disabled: boolean;
  id?: string;
}

const CompoundDetails: React.FC<CompoundDetailsProps> = ({ data, eventHandlers }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isCompoundExpanded, setIsCompoundExpanded] = useState<boolean>(true);
  const [editMode, setEditMode] = useState<boolean[]>(Array(3).fill(false));
  const [headerInputs, setHeaderInputs] = useState<FormElement<string>[]>(
    Array(3).fill({ value: '', isValid: false, disabled: false, id: undefined })
  );
  const [descriptionInputs, setDescriptionInputs] = useState<FormElement<string>[]>(
    Array(3).fill({ value: '', isValid: false, changed: true, id: undefined })
  );
  const [titleArInputs, setTitleArInputs] = useState<FormElement<string>[]>(
    Array(3).fill({ value: '', isValid: false, disabled: false, id: undefined })
  );
  const [descriptionArInputs, setDescriptionArInputs] = useState<FormElement<string>[]>(
    Array(3).fill({ value: '', isValid: false, changed: true, id: undefined })
  );
  const [imagesList, setImagesList] = useState<ImageListType>([]);

  useEffect(() => {
    if (data.compound) {
      setHeaderInputs(
        data.compound.map((detail) => ({
          value: detail.title,
          isValid: true,
          disabled: !!detail.title,
          id: detail?.id
        }))
      );

      setDescriptionInputs(
        data.compound.map((detail) => ({
          value: detail.description,
          isValid: true,
          disabled: !!detail.description,
          id: detail?.id
        }))
      );

      setTitleArInputs(
        data.compound.map((detail) => ({
          value: detail.titleAr || '',
          isValid: true,
          disabled: !!detail.titleAr,
          id: detail?.id
        }))
      );

      setDescriptionArInputs(
        data.compound.map((detail) => ({
          value: detail.descriptionAr || '',
          isValid: true,
          disabled: !!detail.descriptionAr,
          id: detail?.id
        }))
      );

      setImagesList(
        data.compound.map((detail) => ({
          dataURL: detail.image || '',
          file: undefined
        }))
      );

      setEditMode(Array(data.compound.length).fill(false));
    }
  }, [data.compound]);

  const { t } = useTranslation();
  const isMobileView = useWindowResizer();

  const handleHeaderChange = (value: string, isValid: boolean, index: number) => {
    const newHeaders = [...headerInputs];
    newHeaders[index] = { value, isValid, disabled: false, id: newHeaders[index]?.id };
    setHeaderInputs(newHeaders);
  };

  const handleDescriptionChange = (value: string, isValid: boolean, index: number) => {
    const newDescriptions = [...descriptionInputs];
    newDescriptions[index] = { value, isValid, disabled: false, id: newDescriptions[index]?.id };
    setDescriptionInputs(newDescriptions);
  };

  const handleTitleArChange = (value: string, isValid: boolean, index: number) => {
    const newTitlesAr = [...titleArInputs];
    newTitlesAr[index] = { value, isValid, disabled: false, id: newTitlesAr[index]?.id };
    setTitleArInputs(newTitlesAr);
  };

  const handleDescriptionArChange = (value: string, isValid: boolean, index: number) => {
    const newDescriptionsAr = [...descriptionArInputs];
    newDescriptionsAr[index] = {
      value,
      isValid,
      disabled: false,
      id: newDescriptionsAr[index]?.id
    };
    setDescriptionArInputs(newDescriptionsAr);
  };

  const handleImageChange = (imageList: ImageListType, index: number) => {
    const newImageList = [...imagesList];
    newImageList[index] = imageList[0];
    setImagesList(newImageList);
  };

  const handleSubmit = (index: number) => {
    const header = headerInputs[index];
    const description = descriptionInputs[index];
    const image = imagesList[index];
    const descriptionAr = descriptionArInputs[index];
    const titleAr = titleArInputs[index];
    const compoundId = headerInputs[index]?.id;

    const isExistingCompound = Boolean(compoundId);

    if (header.isValid && description.isValid && descriptionAr.isValid && titleAr.isValid) {
      if (isExistingCompound) {
        eventHandlers.updateCompoundDetails(
          compoundId as string,
          header.value,
          description.value,
          titleAr.value,
          descriptionAr.value,
          image?.file
        );
        return;
      }

      if (image && image.file) {
        eventHandlers.uploadCompoundDetails(
          header.value,
          description.value,
          image.file,
          titleAr.value,
          descriptionAr.value
        );
      }
    }
  };

  function handleDeleteClick(id: string | undefined): void {
    if (!id) return;

    eventHandlers.deleteCompoundDetails(id);
  }

  return (
    <>
      <div
        onClick={() => setIsCompoundExpanded(!isCompoundExpanded)}
        className={`${styles['compound-details-container__expand-header']} ${
          isCompoundExpanded
            ? styles['compound-details-container__expand-header--expanded']
            : styles['compound-details-container__expand-header--collapsed']
        }`}
      >
        <h3 className={styles['compound-details-container__expand-title']}>
          {t('PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.COMPOUND_DETAILS')}
        </h3>
        <span className={styles['compound-details-container__expand-arrow']}>▼</span>
      </div>
      {isCompoundExpanded && (
        <div className={styles['compound-details-container']}>
          <div className={styles['compound-details-container__expand-content']}>
            <div className={styles['compound-details-container__tabs']}>
              {[0, 1, 2].map((index) => (
                <button
                  type="button"
                  key={index}
                  className={`${styles['compound-details-container__tab']} ${
                    activeIndex === index ? styles.active : ''
                  }`}
                  onClick={() => setActiveIndex(index)}
                >
                  {t('PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.COMPOUND_SECTION')} {index + 1}
                </button>
              ))}
            </div>

            <div className={styles['compound-details-container__content']}>
              <div className={styles['compound-details-container__field']}>
                <label className={styles['compound-details-container__label']}>
                  {t('PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.COMPOUND_HEADER')}
                </label>
                <InputText
                  config={{
                    autoFocus: false,
                    type: InputTextType.text,
                    required: true,
                    disabled: !!headerInputs[activeIndex]?.disabled && !editMode[activeIndex]
                  }}
                  data={{
                    placeholder: t(
                      'PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.COMPOUND_HEADER_PLACEHOLDER'
                    ),
                    value: headerInputs[activeIndex]?.value || ''
                  }}
                  eventHandlers={{
                    onChangeHandler: (value, isValid) => {
                      handleHeaderChange(value, isValid, activeIndex);
                    }
                  }}
                />
              </div>
              <div className={styles['compound-details-container__field']}>
                <label className={styles['compound-details-container__label']}>
                  العنوان بالعربي (Arabic Title)
                </label>
                <InputText
                  config={{
                    autoFocus: false,
                    type: InputTextType.text,
                    required: false,
                    disabled: !!titleArInputs[activeIndex]?.disabled && !editMode[activeIndex]
                  }}
                  data={{
                    placeholder: 'أدخل العنوان بالعربي',
                    value: titleArInputs[activeIndex]?.value || ''
                  }}
                  eventHandlers={{
                    onChangeHandler: (value, isValid) => {
                      handleTitleArChange(value, isValid, activeIndex);
                    }
                  }}
                />
              </div>
              <div className={styles['compound-details-container__field']}>
                <label className={styles['compound-details-container__label']}>
                  {t('PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.COMPOUND_DESCRIPTION')}
                </label>
                <InputText
                  config={{
                    autoFocus: false,
                    type: InputTextType.textarea,
                    required: true,
                    rows: 5,
                    disabled: !!descriptionInputs[activeIndex]?.disabled && !editMode[activeIndex]
                  }}
                  data={{
                    placeholder: t(
                      'PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.COMPOUND_DESCRIPTION_PLACEHOLDER'
                    ),
                    value: descriptionInputs[activeIndex]?.value || ''
                  }}
                  eventHandlers={{
                    onChangeHandler: (value, isValid) => {
                      handleDescriptionChange(value, isValid, activeIndex);
                    }
                  }}
                />
              </div>
              <div className={styles['compound-details-container__field']}>
                <label className={styles['compound-details-container__label']}>
                  الوصف بالعربي (Arabic Description)
                </label>
                <InputText
                  config={{
                    autoFocus: false,
                    type: InputTextType.textarea,
                    required: false,
                    rows: 5,
                    disabled: !!descriptionArInputs[activeIndex]?.disabled && !editMode[activeIndex]
                  }}
                  data={{
                    placeholder: 'أدخل الوصف بالعربي',
                    value: descriptionArInputs[activeIndex]?.value || ''
                  }}
                  eventHandlers={{
                    onChangeHandler: (value, isValid) => {
                      handleDescriptionArChange(value, isValid, activeIndex);
                    }
                  }}
                />
              </div>
              {/* Image Upload Section */}
              <div className={styles['compound-details-container__field']}>
                <label className={styles['compound-details-container__label']}>
                  {t('PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.COMPOUND_IMAGE')}
                </label>
                <ImageUploading
                  value={imagesList[activeIndex] ? [imagesList[activeIndex]] : []}
                  onChange={(imageList) => handleImageChange(imageList, activeIndex)}
                  maxNumber={1}
                  dataURLKey="dataURL"
                >
                  {({
                    imageList,
                    onImageUpload,
                    onImageUpdate,
                    onImageRemove,
                    isDragging,
                    dragProps
                  }) => (
                    <div className={styles['upload-image-wrapper']}>
                      {imageList.length > 0 ? (
                        <div className={styles['image-container']}>
                          <img src={imageList[0].dataURL} alt="Compound" />
                          <div
                            className={styles['image-controls']}
                            style={{
                              display:
                                imagesList[activeIndex] &&
                                descriptionInputs[activeIndex]?.disabled &&
                                headerInputs[activeIndex]?.disabled
                                  ? 'none'
                                  : 'flex'
                            }}
                          >
                            <button type="button" onClick={() => onImageUpdate(0)}>
                              <EditIcon />
                            </button>
                            <button type="button" onClick={() => onImageRemove(0)}>
                              <DeleteIcon />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className={styles['dropzone']}
                          {...dragProps}
                          onClick={onImageUpload}
                          style={isDragging ? { border: '2px dashed #4a90e2' } : undefined}
                        >
                          <CloudUploadIcon fontSize="large" />
                          <span>{t('PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.UPLOAD_IMAGE')}</span>
                        </div>
                      )}
                    </div>
                  )}
                </ImageUploading>
              </div>

              {imagesList[activeIndex] &&
              descriptionInputs[activeIndex]?.disabled &&
              headerInputs[activeIndex]?.disabled &&
              !editMode[activeIndex] ? (
                <div className={styles['compound-details-container__actions']}>
                  <Button
                    type={ButtonType.gray}
                    onClick={() => {
                      const next = [...editMode];
                      next[activeIndex] = true;
                      setEditMode(next);
                    }}
                    tracking={{
                      action: 'edit',
                      category: 'compound-details',
                      label: 'compound-details-edit'
                    }}
                    size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                  >
                    Edit
                  </Button>
                  <Button
                    type={ButtonType.danger}
                    onClick={() => handleDeleteClick(headerInputs[activeIndex]?.id)}
                    tracking={{
                      action: 'delete',
                      category: 'compound-details',
                      label: 'compound-details-delete'
                    }}
                    size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                  >
                    {t('PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.DELETE_COMPOUND_DETAILS')}
                  </Button>
                </div>
              ) : (
                <div className={styles['compound-details-container__actions']}>
                  {headerInputs[activeIndex]?.id && editMode[activeIndex] && (
                    <Button
                      type={ButtonType.gray}
                      onClick={() => {
                        const next = [...editMode];
                        next[activeIndex] = false;
                        setEditMode(next);
                      }}
                      tracking={{
                        action: 'cancel',
                        category: 'compound-details',
                        label: 'compound-details-cancel'
                      }}
                      size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    onClick={() => handleSubmit(activeIndex)}
                    tracking={{
                      action: 'submit',
                      category: 'compound-details',
                      label: 'compound-details-update'
                    }}
                    disabled={
                      !headerInputs[activeIndex]?.isValid ||
                      !descriptionInputs[activeIndex]?.isValid ||
                      (headerInputs[activeIndex]?.id ? false : !imagesList[activeIndex]?.dataURL)
                    }
                    size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                  >
                    {t('PROPERTY_MODULE.EDIT_GENERAL_CONTAINER.UPDATE_COMPOUND_DETAILS')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompoundDetails;
